import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { COMPANY, CONTACT } from "@/lib/constants";
import { useConnectModal } from "@/context/ConnectModalContext";
import LottiePlayer from "@/components/LottiePlayer";
import rippleAnim from "@/assets/lottie/reach-ripple.json";

const Addr = ({ title, lines }) => (
  <div>
    <h2 className="hp-h3">{title}</h2>
    <address className="not-italic mt-3 hp-body text-[15px]">
      {lines.map((l) => <div key={l}>{l}</div>)}
    </address>
  </div>
);

export default function Contact() {
  const { openConnectModal } = useConnectModal();
  const row = "flex items-center gap-3 py-4 border-b border-slate-200 font-semibold text-hop-blue hover:text-[var(--hop-brand)] transition-colors";
  return (
    <>
      <Seo title="Contact"
        description="Contact House of Projects for an executive conversation about systems, visibility and hiring. Registered office and principal place of business listed."
        path="/contact" />
      <section data-testid="contact-hero" className="relative overflow-hidden">
        <div className="hp-hero-grid" aria-hidden="true" />
        <div className="hop-container relative py-16 md:py-24 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="hp-label text-[var(--hop-brand)]">Contact</p>
              <h1 className="hp-h1 mt-5">Start with a conversation.</h1>
              <p className="hp-lead mt-7">
                Tell us where growth is constrained. We take on a limited number of engagements and begin with research, not a proposal template.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <button type="button" onClick={openConnectModal} className="hp-btn hp-btn-primary" data-testid="contact-cta">
                  Request an executive conversation <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <Link to="/consultation" className="hp-btn hp-btn-ghost">Structured consultation</Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={120} className="lg:col-span-5">
            <LottiePlayer className="w-40 h-40 -mt-6 mb-2 hidden lg:block" data={rippleAnim} label="Signal ripple: we respond to every enquiry" />
            <div className="border-t border-slate-200">
              <a href={CONTACT.emailHref} className={row}><Mail className="w-5 h-5" aria-hidden="true" />{CONTACT.email}</a>
              <a href={CONTACT.phoneHref} className={row}><Phone className="w-5 h-5" aria-hidden="true" />{CONTACT.phoneDisplay}</a>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className={row}><MessageCircle className="w-5 h-5" aria-hidden="true" />WhatsApp</a>
            </div>
          </Reveal>
        </div>
        <div className="hp-rule" aria-hidden="true" />
      </section>
      <section className="hp-section hp-tint" aria-label="Office addresses">
        <div className="hop-container">
          <Reveal>
            <div className="grid md:grid-cols-2 gap-12">
              <Addr title="Office" lines={COMPANY.registeredOffice} />
              {COMPANY.principalPlace.length > 0 && (
                <Addr title="Principal place of business" lines={COMPANY.principalPlace} />
              )}
            </div>
            <p className="mt-10 text-sm text-slate-500">
              {COMPANY.legalName}. <Link to="/company-information" className="hp-link-u font-semibold text-hop-blue">Company information</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
